// src/encounter/services/use-care-plans.ts
// The patient's care plans for the encounter's future care-plan card (contract C5). Data layer only:
// nothing mounts this yet, so it issues no request until the card that consumes it exists.
//
// The card must keep four outcomes apart, and this hook hands them over distinctly:
//   isLoading          → still reading
//   isError            → the read failed (includes a 404: the endpoint is missing, or the patient is
//                        in another organisation; and a payload that breaks the contract). NEVER
//                        "no care plan".
//   data = []          → the patient has no care plan. The only outcome that is a clinical statement.
//   data = [...]       → the plans, as `CarePlanView` (see `../lib/care-plan-view.ts`).
// No patient id → the query is idle (not loading), exactly like the other patient-scoped reads.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { toCarePlanViews, withCurrentSteps, type CarePlanView } from '../lib/care-plan-view';
import { fetchCarePlans, fetchPatientEnrolments } from './care-model-api';

export function carePlansKey(patientId: string | null | undefined) {
  return ['care-plans', patientId] as const;
}

export function useCarePlans(patientId: string | null | undefined): UseQueryResult<CarePlanView[]> {
  return useQuery<CarePlanView[]>({
    queryKey: carePlansKey(patientId),
    enabled: !!patientId,
    // Mapped inside the queryFn, so a contract break is an ordinary failed read.
    queryFn: async ({ signal }) => (patientId ? toCarePlanViews(await fetchCarePlans(patientId, signal)) : []),
  });
}

export function patientEnrolmentsKey(patientId: string | null | undefined) {
  return ['care-model-enrolments', patientId] as const;
}

/** What the encounter's care-plan readout renders from (design desk D-14 / D-43). */
export type CarePlanReadoutState =
  | { kind: 'hidden' } // loading, idle, or no care plan: D-14 renders nothing
  | { kind: 'error' } // the C5 read failed: one line in words
  | {
      kind: 'plans';
      plans: CarePlanView[];
      /** The C4 step read behind `currentStep`: 'pending' and 'failed' leave every step 'not-wired'. Optional, so a state
       *  built without it (tests, stories) reads as before. `?x=prevent-honest` withholds a count it cannot check. */
      steps?: 'pending' | 'failed' | 'read';
    };

/**
 * C5 plans + C4 current steps. C5 decides what shows; a failed or pending C4 read only leaves the
 * step out ('not-wired'), it never hides the plan.
 */
export function useCarePlanReadout(patientId: string | null | undefined): CarePlanReadoutState {
  const plans = useCarePlans(patientId);
  const enrolments = useQuery({
    queryKey: patientEnrolmentsKey(patientId),
    enabled: !!patientId && (plans.data?.length ?? 0) > 0,
    queryFn: ({ signal }) => (patientId ? fetchPatientEnrolments(patientId, signal) : Promise.resolve([])),
  });
  if (plans.isError) return { kind: 'error' };
  if (!plans.data || plans.data.length === 0) return { kind: 'hidden' };
  return {
    kind: 'plans',
    plans: withCurrentSteps(plans.data, enrolments.isSuccess ? enrolments.data : null),
    steps: stepReadState(enrolments),
  };
}

/**
 * The C4 step read as the readout reports it: 'pending' only while a read is running. A query that is not running and has no
 * answer (disabled: React Query's `status: 'pending'` with `fetchStatus: 'idle'`) has no read coming, so it reports 'failed'
 * (no step is known) rather than 'pending' forever.
 */
export function stepReadState(q: { isSuccess: boolean; isError: boolean; fetchStatus: 'fetching' | 'paused' | 'idle' }): 'pending' | 'failed' | 'read' {
  if (q.isSuccess) return 'read';
  if (q.isError) return 'failed';
  return q.fetchStatus === 'idle' ? 'failed' : 'pending';
}
